import { Box, Collapse, Icon, IconButton, LinearProgress, Stack, Typography } from "@mui/material";
import { User } from "../../../../types/User";
import { getSpacedFromCamel_Cap } from "../../../../util/string";
import { useState } from "react";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { ExpandMore } from "@mui/icons-material";
import { useLogin } from "../../../../context/Login/LoginContext";

type ProfileCompletenessProps = {
    user: User | null;
}

const requiredFields = [
    "fullName",
    "email",
    "role",
    "image",
    "gender",
    "birthDate",
    "location",
    "jobTitle",
    "jobDescription",
    "skills",
    "description",
    "jobLookingFor",
    "jobLookingForType",
    "jobLookingForLocation",
    "jobLookingForSalary",
    "education",
    "workHistory",
] as const;

const getCompletionPercentage = (user: User | null) => {
    if (!user) return 0;
    let completionPercentage = 0;
    const eachPercentage = 100 / requiredFields.length;

    requiredFields.forEach(field => {
        if (user[field]) completionPercentage += eachPercentage;
    });

    return completionPercentage;
}

const ProfileCompleteness = ({ user }: ProfileCompletenessProps) => {
    const completionPercentage = getCompletionPercentage(user);
    const roundedPercentage = Math.round(completionPercentage);
    const missingFields = requiredFields.filter(field => !user?.[field]);
    const [expanded, setExpanded] = useState(false);
    const { user: self } = useLogin();

    const Details = () => {
        const isSelf = user?._id === self?._id;

        return (
            <>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1} mb={2}
                    sx={{
                        cursor: "pointer",
                        "&:hover": {
                            color: "success.main",
                        }
                    }}
                    onClick={() => setExpanded(!expanded)}
                >
                    <Typography variant="body2" color="inherit">
                        {
                            !isSelf ?
                                "Content is not set by the user yet" :
                                roundedPercentage < 50 ?
                                    "Get more work by improving your profile!" :
                                    roundedPercentage < 80 ?
                                        "Your profile is almost complete!" :
                                        roundedPercentage < 100 ?
                                            "Your profile is complete, but you can still improve it!" :
                                            "Your profile is complete!"
                        }
                    </Typography>
                    {expanded ? <ExpandLess /> : <ExpandMore />}
                </Stack>

                <Collapse in={expanded} timeout="auto" unmountOnExit sx={{ width: '100%', maxWidth: '100%' }}>
                    {missingFields.map(field => (
                        <Typography key={field} variant="body2" color="text.secondary">
                            {isSelf ? "- add your" : "-"} {getSpacedFromCamel_Cap(field)}
                        </Typography>
                    ))}
                </Collapse>
            </>
        )
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1">
                    Profile Completeness
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold" color="success">
                    {roundedPercentage}%
                </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
                <LinearProgress
                    variant="determinate"
                    value={completionPercentage}
                    sx={{
                        height: 10,
                        borderRadius: 5,
                        backgroundColor: "#eee",
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: "#14a800",
                            borderRadius: 5
                        }
                    }}
                />
            </Box>

            <Details />
        </Box>
    );
};

export default ProfileCompleteness; 